import { useEffect } from 'react';
import { View, Platform, useWindowDimensions } from 'react-native';
import { initFonts } from './styles/fonts';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

initFonts();
import { useAppStore } from './store/appStore';
import { resolveTheme, LightColors, DarkColors } from './styles/theme';
import TopBar from './components/TopBar';
import DrawerLayout from './components/DrawerLayout';
import ReadingPositionIndicator from './components/ReadingPositionIndicator';
import ChapterList from './components/ChapterList';
import SettingsPanel from './components/SettingsPanel';
import FootnoteModal from './components/FootnoteModal';
import NotifyModal from './components/NotifyModal';
import RecitationPlayer from './components/RecitationPlayer';
import SearchBar from './components/SearchBar';
import HomePage from './pages/HomePage';
import VersePage from './pages/VersePage';
import { useScrollTopBar } from './hooks/useScrollTopBar';
import { useFridayKahf } from './hooks/useFridayKahf';
import { useNoSleep } from './hooks/useNoSleep';
import { showSnackbar, hideSnackbar } from './services/snackbar';

function AppShell() {
  const { width, height } = useWindowDimensions();
  const theme = useAppStore((s) => s.settings.theme);
  const autoSwitch = useAppStore((s) => s.settings.auto_switch_theme);
  const viewMode = useAppStore((s) => s.settings.view_mode.value);
  const setOnline = useAppStore((s) => s.setOnline);
  const appTitle = useAppStore((s) => s.appTitle);
  const dontNotify = useAppStore((s) => s.dontNotify);
  const setShowNotifyModal = useAppStore((s) => s.setShowNotifyModal);
  const navigate = useNavigate();
  const location = useLocation();

  useScrollTopBar();
  useFridayKahf(navigate);
  useNoSleep();

  useEffect(() => {
    if (dontNotify === 0) {
      setShowNotifyModal(true);
    }
  }, [dontNotify, setShowNotifyModal]);

  // Document title (web only)
  useEffect(() => {
    if (Platform.OS === 'web') {
      document.title = appTitle ? `${appTitle} - Guided Verses` : 'Guided Verses';
    }
  }, [appTitle, location.pathname]);

  // Theme application (web only – on native you'd use a ThemeContext)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const html = document.documentElement;
    const applyTheme = () => {
      html.classList.remove('light', 'dark');
      let value = theme.value;
      if (value === 'system') {
        value = window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      } else if (autoSwitch) {
        const hour = new Date().getHours();
        value = hour >= 6 && hour < 18 ? 'light' : 'dark';
      }
      html.classList.add(value);
    };
    applyTheme();

    let mql: MediaQueryList | null = null;
    const handleChange = () => applyTheme();
    if (theme.value === 'system' && window.matchMedia) {
      mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.addEventListener?.('change', handleChange);
    }
    return () => { mql?.removeEventListener?.('change', handleChange); };
  }, [theme, autoSwitch]);

  // Sync <meta name="theme-color"> with topbar color
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    let color: string;
    if (viewMode === 'mushaf') {
      color = '#2D2D2D';
    } else {
      const resolved = resolveTheme(theme.value, autoSwitch);
      color = resolved === 'dark' ? DarkColors.topBarBg : LightColors.topBarBg;
    }
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color);
  }, [theme, autoSwitch, viewMode]);

  // Online/offline (web only)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const goOnline = () => { setOnline(true); hideSnackbar(); };
    const goOffline = () => { setOnline(false); showSnackbar('Offline', 0); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [setOnline]);

  // Keyboard shortcut "S" for search (web only)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const handleKeyUp = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (e.key === 's' || e.key === 'S') {
        if (tag !== 'INPUT' && tag !== 'TEXTAREA') {
          if (window.innerWidth <= 768) {
            useAppStore.getState().toggleLeftDrawer();
          }
          useAppStore.getState().setCardInput('search', '');
        }
      } else if (e.key === 'Escape') {
        useAppStore.getState().setCardInput('', '');
      }
    };
    window.addEventListener('keyup', handleKeyUp);
    return () => window.removeEventListener('keyup', handleKeyUp);
  }, []);

  const leftContent = <ChapterList />;
  const rightContent = <SettingsPanel />;

  return (
    <View style={{ width, height, overflow: 'hidden' }}>
      <TopBar />
      <SearchBar />
      <ReadingPositionIndicator />
      <DrawerLayout leftContent={leftContent} rightContent={rightContent} overlay={<RecitationPlayer />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/:ch/:vs" element={<VersePage />} />
          <Route path="/:ch" element={<VersePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </DrawerLayout>
      <FootnoteModal />
      <NotifyModal />
    </View>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
